import {
    BedIcon,
    BuildingIcon,
    ExpandIcon,
    LandPlotIcon,
    ShowerHeadIcon,
} from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { Accordion } from '@/components/molecules';
import { useTranslation, useUIColors } from '@/core/hooks';
import React from 'react';
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useMobileAmenities } from '../hooks/useMobileAmenities';
import { WebAmenityIcon } from './WebAmenityIcon';

export interface AboutPropertyProps {
    /** Omit/0 hides the Bedrooms stat instead of showing a placeholder. */
    bedrooms?: number | null;
    /** Omit/0 hides the Bathrooms stat instead of showing a placeholder. */
    bathrooms?: number | null;
    /** Omit/0 hides the Area stat instead of showing a placeholder. */
    areaSqFt?: number | null;
    amenities: string[];
    onViewFloorPlan?: () => void;
    floorPlanLoading?: boolean;
    isFloorPlanAvailable?: boolean;
}

// Memoized: the property details screen re-renders on every scroll drag (it
// toggles isPageScrolling to pause the hero carousel). Without this, each drag
// re-rendered every amenity icon mid-load — on iOS that froze the UI.
export const AboutProperty = React.memo(function AboutProperty({
    bedrooms,
    bathrooms,
    areaSqFt,
    amenities,
    onViewFloorPlan,
    floorPlanLoading = false,
    isFloorPlanAvailable = false
}: Readonly<AboutPropertyProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();

    const hasBedrooms = Boolean(bedrooms);
    const hasBathrooms = Boolean(bathrooms);
    const hasArea = Boolean(areaSqFt);
    const hasUnitInfo = hasBedrooms || hasBathrooms || hasArea;
    const amenityItems = useMobileAmenities(amenities);

    const renderAmenities = () => {
        const rows = [];
        for (let i = 0; i < amenityItems.length; i += 3) {
            const rowAmenities = amenityItems.slice(i, i + 3);
            rows.push(
                <View key={rowAmenities[0]?.value ?? i} style={styles.amenitiesRow}>
                    {rowAmenities.map(({ value, label, iconUrl }) => (
                        <View key={value} style={styles.amenityItem}>
                            <WebAmenityIcon
                                iconUrl={iconUrl}
                                label={label}
                                size={24}
                                defaultColor={colors.fontTertiary}
                            />
                            <Typography variant="footnote" color="tertiary" style={styles.amenityText}>
                                {label}
                            </Typography>
                        </View>
                    ))}
                </View>
            );
        }
        return rows;
    };

    return (
        <Accordion
            icon={BuildingIcon}
            title={t('propertyDetails.aboutUnit')}
            defaultExpanded={true}
        >
            <View style={styles.content}>
                <Typography variant="headline" color="primary" style={styles.sectionTitle}>
                    {t('propertyDetails.unitInfo')}
                </Typography>

                {hasUnitInfo && (
                    <View style={styles.propertyInfo}>
                        {hasBedrooms && (
                            <View style={styles.propertyItem}>
                                <View style={styles.propertyItemHeader}>
                                    <BedIcon size={22} color={colors.fontPrimary} />
                                    <Typography variant="title2" color="primary" style={styles.propertyValue}>
                                        {bedrooms}
                                    </Typography>
                                </View>
                                <Typography variant="caption" color="secondary" style={styles.propertyItemLabel}>
                                    {t('propertyDetails.bedrooms')}
                                </Typography>
                            </View>
                        )}

                        {hasBathrooms && (
                            <View style={styles.propertyItem}>
                                <View style={styles.propertyItemHeader}>
                                    <ShowerHeadIcon size={22} color={colors.fontPrimary} />
                                    <Typography variant="title2" color="primary" style={styles.propertyValue}>
                                        {bathrooms}
                                    </Typography>
                                </View>
                                <Typography variant="caption" color="secondary" style={styles.propertyItemLabel}>
                                    {t('propertyDetails.bathrooms')}
                                </Typography>
                            </View>
                        )}

                        {hasArea && (
                            <View style={styles.propertyItem}>
                                <View style={styles.propertyItemHeader}>
                                    <ExpandIcon size={22} color={colors.fontPrimary} />
                                    <Typography variant="title2" color="primary" style={styles.propertyValue}>
                                        {areaSqFt!.toLocaleString()}
                                    </Typography>
                                </View>
                                <Typography variant="caption" color="secondary" style={styles.propertyItemLabel}>
                                    {t('propertyDetails.areaInSqFt')}
                                </Typography>
                            </View>
                        )}
                    </View>
                )}

                {isFloorPlanAvailable && (
                    <View style={styles.floorPlanButtonWrapper}>
                        <TouchableOpacity
                            style={styles.floorPlanButton}
                            onPress={floorPlanLoading ? undefined : onViewFloorPlan}
                            disabled={floorPlanLoading}
                        >
                            <LandPlotIcon size={20} color="#1A1A1A" />
                            <Typography variant="headline" style={styles.floorPlanText}>
                                {t('propertyDetails.viewFloorPlan')}
                            </Typography>
                        </TouchableOpacity>
                        {floorPlanLoading && (
                            <View style={[styles.floorPlanLoadingOverlay, { backgroundColor: colors.backgroundLevel1 }]}>
                                <ActivityIndicator size="small" color={colors.fontPrimary} />
                            </View>
                        )}
                    </View>
                )}

                {amenityItems.length > 0 && (
                    <View style={styles.amenitiesSection}>
                        <Typography variant="headline" color="primary" style={[styles.sectionTitle, { marginBottom: 16 }]}>
                            {t('propertyDetails.amenities')}
                        </Typography>

                        <View style={styles.amenitiesContainer}>
                            {renderAmenities()}
                        </View>
                    </View>
                )}
            </View>
        </Accordion>
    );
});

const styles = StyleSheet.create({
    content: {
        gap: -1,
        paddingTop: 8,
    },
    sectionTitle: {
        fontWeight: '700',
        fontSize: 16,
        marginBottom: 8,
    },
    propertyInfo: {
        flexDirection: 'row',
        gap: 12,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        paddingVertical: 12,
    },
    propertyItem: {
        flex: 1,
        alignItems: 'center',
        gap: 10,
    },
    propertyItemHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    propertyValue: {
        fontWeight: '700',
        fontSize: 18,
    },
    propertyItemLabel: {
        textAlign: 'center',
        fontSize: 12,
        color: '#666666',
    },
    floorPlanButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 16,
    },
    floorPlanButtonWrapper: {
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 16,
        marginVertical:8,
    },
    floorPlanLoadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 4,
        opacity: 0.85,
    },
    floorPlanText: {
        fontWeight: '600',
        textDecorationLine: 'underline',
        fontSize: 16,
        lineHeight: 16,
        color: '#1A1A1A',
        letterSpacing: 0,
    },
    amenitiesSection: {
        marginTop: 8,
        paddingBottom: 16,
    },
    amenitiesContainer: {
        gap: 20,
    },
    amenitiesRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    amenityItem: {
        flex: 1,
        alignItems: 'center',
        gap: 18,
        paddingHorizontal: 4,
    },
    amenityText: {
        textAlign: 'center',
        fontSize: 12,
        color: '#666666',
        lineHeight: 14,
    },
});

export default AboutProperty;
